// ─────────────────────────────────────────────────────────────
//  XYLO — Auth Context
//  Wraps Firebase Auth — real user session, persists across reloads
// ─────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { auth, provider } from "../lib/firebase";
import { createUserProfile, getUserProfile } from "../lib/db";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(undefined); // undefined = loading
  const [profile, setProfile]           = useState(null);
  const [loading, setLoading]           = useState(true);

  // ── Listen to Firebase auth state ───────────────────────────
  // This is the key: Firebase remembers the session automatically.
  // Just like Instagram/WhatsApp — user stays logged in across reloads.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser);
        // Load or create their Firestore profile
        const prof = await getUserProfile(fbUser.uid);
        setProfile(prof);
      } else {
        setFirebaseUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Real Google Sign-In ──────────────────────────────────────
  // Opens the real Google account picker in a popup
  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, provider);
    const fbUser = result.user;
    const av = fbUser.displayName
      ? fbUser.displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
      : fbUser.email.slice(0, 2).toUpperCase();

    const prof = await createUserProfile(fbUser.uid, {
      name:        fbUser.displayName || fbUser.email.split("@")[0],
      email:       fbUser.email,
      avatar:      av,
      loginMethod: "google",
      photoURL:    fbUser.photoURL || null,
    });
    setProfile(prof);
    return prof;
  };

  // ── Email Sign Up ────────────────────────────────────────────
  const signUpWithEmail = async (name, email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const fbUser = result.user;
    // Set display name in Firebase Auth
    await updateProfile(fbUser, { displayName: name });
    const av = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

    const prof = await createUserProfile(fbUser.uid, {
      name,
      email,
      avatar:      av,
      loginMethod: "email",
    });
    setProfile(prof);
    return prof;
  };

  // ── Email Sign In ────────────────────────────────────────────
  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const prof   = await getUserProfile(result.user.uid);
    setProfile(prof);
    return prof;
  };

  // ── Sign Out ─────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setProfile(null);
  };

  // ── Refresh profile ──────────────────────────────────────────
  const refreshProfile = async () => {
    if (firebaseUser) {
      const prof = await getUserProfile(firebaseUser.uid);
      setProfile(prof);
    }
  };

  return (
    <AuthContext.Provider value={{
      firebaseUser,
      profile,
      loading,
      loginWithGoogle,
      signUpWithEmail,
      loginWithEmail,
      logout,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
